import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAxios } from './use-axios';

const SETTINGS_QUERY_KEY = ['settings'];

interface ApiSuccessResponse {
  status: 'success';
  data: {
    key: string;
    value: string;
  };
}

interface ApiError {
  error?: {
    message?: string;
    status?: number;
  };
  details?: Array<{ message: string }>;
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError<ApiError>(error)) {
    if (error.response?.data?.details) {
      return 'Please check your input and try again';
    }
    if (error.response?.status === 500) {
      return 'An internal server error occurred. Please try again later';
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'You do not have permission to perform this action';
    }
    if (error.response?.status === 404) {
      return 'Setting not found';
    }
    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection';
    }
  }
  return 'An unexpected error occurred. Please try again';
}

// Get a global setting (no auth required)
export function useGetGlobalSetting(key: string, options?: { enabled?: boolean }) {
  const axios = useAxios();
  return useQuery({
    queryKey: [...SETTINGS_QUERY_KEY, 'global', key],
    queryFn: async () => {
      try {
        const { data } = await axios.get<ApiSuccessResponse>(`/settings/global/${encodeURIComponent(key)}`);
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    enabled: !!key && (options?.enabled ?? true),
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

// Set a global setting (no auth required)
export function useSetGlobalSetting() {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      try {
        const { data } = await axios.post<ApiSuccessResponse>(`/settings/global/${encodeURIComponent(key)}`, { value });
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: [...SETTINGS_QUERY_KEY, 'global', variables.key] });
    },
  });
}

// Get a user setting (auth required)
export function useGetUserSetting(key: string, options?: { enabled?: boolean }) {
  const axios = useAxios();
  return useQuery({
    queryKey: [...SETTINGS_QUERY_KEY, 'user', key],
    queryFn: async () => {
      try {
        const { data } = await axios.get<ApiSuccessResponse>(`/settings/user/${encodeURIComponent(key)}`);
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    enabled: !!key && (options?.enabled ?? true),
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

// Set a user setting (auth required)
export function useSetUserSetting() {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      try {
        const { data } = await axios.post<ApiSuccessResponse>(`/settings/user/${encodeURIComponent(key)}`, { value });
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: [...SETTINGS_QUERY_KEY, 'user', variables.key] });
    },
  });
}
